import React, { useState, useEffect, useMemo } from "react";
import {
  Container,
  Paper,
  Typography,
  Button,
  Box,
  Stack,
  Divider,
  Chip,
  CircularProgress,
  Card,
  CardMedia,
  Grid,
  Rating,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import HotelIcon from "@mui/icons-material/Hotel";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import PeopleOutlineIcon from "@mui/icons-material/PeopleOutline";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";
import TimerOutlinedIcon from "@mui/icons-material/TimerOutlined";
import CloseIcon from "@mui/icons-material/Close";
import PaymentOutlinedIcon from "@mui/icons-material/PaymentOutlined";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import { useLocation, useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { searchAPI } from "../API/Seach";
import MOCK_ITINERARY_DETAILS from "../data/itineraryDetailsMock.json";
import { tokens } from "../theme/tokens";
import {
  GuestDecorLeftColumn,
  GuestDecorRightColumn,
} from "../Components/GuestDetail/GuestDetailSideDecor";

const themePrimary = tokens.brand;
const themeAccent = tokens.accent;

/** Persisted for /payment-status → book API after payment (same key in PaymentStatus.js). */
const PENDING_BOOK_STORAGE_KEY = "tripTrip_pendingBookContext";

function getHotelHeroImage(hotel) {
  if (!hotel?.images?.length) return null;
  const first = hotel.images[0];
  const xl = first?.links?.find((l) => l.size === "Xl" || l.size === "XL");
  return xl?.url || first?.links?.[0]?.url;
}

function formatInr(amount) {
  if (amount == null || Number.isNaN(Number(amount))) return "—";
  return `₹${Number(amount).toLocaleString("en-IN")}`;
}

function formatStayDate(iso) {
  if (!iso || iso === "—") return "—";
  try {
    return new Date(iso).toLocaleDateString("en-IN", {
      weekday: "short",
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  } catch {
    return iso;
  }
}

/** e.g. "4 Apr 2026, 11:00 pm" for cancellation windows */
function formatCancellationDateTime(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-GB", {
      day: "numeric",
      month: "short",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
  } catch {
    return String(iso);
  }
}

function formatCancellationCharge(rule) {
  const vt = String(rule?.valueType || "").toLowerCase();
  if (vt === "percentage") {
    const v = rule.value ?? rule.estimatedValue;
    if (v == null || Number.isNaN(Number(v))) return "—";
    return `${Number(v)}%`;
  }
  const amount = rule.value ?? rule.estimatedValue;
  if (amount == null || Number.isNaN(Number(amount))) return "—";
  const n = Number(amount);
  const formatted = n.toLocaleString("en-IN", {
    minimumFractionDigits: Number.isInteger(n) ? 0 : 2,
    maximumFractionDigits: 2,
  });
  return `₹${formatted}`;
}

/** Supports `data.rooms[]` (multi-room) and legacy `data.room` + `data.rate`. */
function normalizeItineraryRooms(data) {
  if (!data) return [];
  if (Array.isArray(data.rooms) && data.rooms.length > 0) {
    return data.rooms.map((r, i) => ({
      index: i + 1,
      name: r?.name || "—",
      description: r?.description,
      rate: r?.rate,
      guests: Array.isArray(r?.guests) ? r.guests : [],
    }));
  }
  if (data.room) {
    return [
      {
        index: 1,
        name: data.room.name || "—",
        description: data.room.description,
        rate: data.rate,
        guests: Array.isArray(data.guests) ? data.guests : [],
      },
    ];
  }
  return [];
}

function aggregateRatesFromRooms(roomsNormalized) {
  if (!roomsNormalized?.length) return null;
  let baseRate = 0;
  let finalRate = 0;
  let taxAmount = 0;
  let refundable = true;
  let currency = null;
  let boardBasis = null;
  for (const entry of roomsNormalized) {
    const r = entry.rate;
    if (!r) continue;
    if (currency == null) currency = r.currency;
    if (boardBasis == null) boardBasis = r.boardBasis;
    baseRate += Number(r.baseRate) || 0;
    finalRate += Number(r.finalRate) || 0;
    taxAmount += Number(r.taxAmount) || 0;
    if (r.refundable === false) refundable = false;
  }
  if (!baseRate && !finalRate && taxAmount === 0) return null;
  return {
    baseRate,
    finalRate,
    taxAmount,
    currency: currency || "INR",
    refundable,
    boardBasis,
  };
}

function guestDisplayName(g) {
  const parts = [g?.title, g?.firstName, g?.lastName].filter(
    (x) => x != null && String(x).trim() !== ""
  );
  return parts.length ? parts.join(" ").replace(/\s+/g, " ").trim() : "—";
}

function flattenCancellationRows(roomsNormalized) {
  const rows = [];
  for (const room of roomsNormalized) {
    const rules = room.rate?.cancellationPolicies?.[0]?.rules;
    if (!Array.isArray(rules) || !rules.length) continue;
    for (const rule of rules) {
      rows.push({ roomLabel: `Room ${room.index}`, rule });
    }
  }
  return rows;
}

/** Backend callback URL using API base (same host as REACT_APP_SEARCH_API_URL). */
// function getPaymentReturnUrl() {
//   const base = (process.env.REACT_APP_SEARCH_API_URL || "").replace(/\/$/, "");
//   if (!base) return `${window.location.origin}/api/callback`;
//   return `${base}/api/callback`;
// }
function getPaymentReturnUrl() {
  const base = (process.env.REACT_APP_SEARCH_API_URL || "").replace(/\/$/, "");
  if (!base) return `${window.location.origin}/api/payment/callback`;
  return `${base}/api/payment/callback`;
}

function splitNameFromLoginEmail(emailOrUserName) {
  const raw = (emailOrUserName || "").trim();
  const local = raw.includes("@") ? raw.split("@")[0] : raw || "Guest";
  const parts = local.replace(/[._]+/g, " ").split(/\s+/).filter(Boolean);
  return {
    first_name: parts[0] || "Guest",
    last_name: parts.slice(1).join(" ") || "User",
  };
}

function isHdfcPaymentMode(mode) {
  const code = String(mode?.code || "").toLowerCase();
  const name = String(mode?.paymentModeName || "").toLowerCase();
  return code === "hdfc" || name.includes("hdfc");
}

function isPhonePePaymentMode(mode) {
  const code = String(mode?.code || "").toLowerCase().replace(/\s+/g, "");
  const name = String(mode?.paymentModeName || "").toLowerCase().replace(/\s+/g, "");
  return (
    code.includes("phonepe") ||
    code.includes("phone_pay") ||
    name.includes("phonepe") ||
    name.includes("phonepay")
  );
}

function isWalletPaymentMode(mode) {
  const code = String(mode?.code || "").toLowerCase();
  const name = String(mode?.paymentModeName || "").toLowerCase();
  return code.includes("wallet") || name.includes("wallet");
}

/** Same sources as `Payment.js` for consistency. */
const PAYMENT_BRAND_HDFC_IMG =
  "https://logosmarcas.net/wp-content/uploads/2020/11/HDFC-Bank-Emblema.png";
const PAYMENT_BRAND_PHONEPE_IMG =
  "https://i1.wp.com/logos-download.com/wp-content/uploads/2021/01/PhonePe_Logo_full.png";

/** Normalizes search `query` for POST /api/Hotel/order/create-order-from-itinerary */
function buildCreateOrderQuery(bookingQuery, traceIdFromRoute) {
  const userIdFromStorage = parseInt(localStorage.getItem("userId"), 10);
  const q = bookingQuery || {};
  return {
    userId: q.userId ?? q.UserId ?? (Number.isFinite(userIdFromStorage) ? userIdFromStorage : 0),
    locationId: q.locationId ?? 0,
    hotelId: Array.isArray(q.hotelId) ? q.hotelId : [],
    checkIn: q.checkIn,
    checkOut: q.checkOut,
    occupancies: q.occupancies || [],
    filterBy: {
      freeBreakfast: false,
      isRefundable: false,
      ratings: [],
      tags: [],
      reviewRatings: [],
      ...(q.filterBy || {}),
    },
    page: q.page ?? 1,
    traceId: traceIdFromRoute || q.traceId || "",
  };
}

const ReviewBooking = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    itineraryCode,
    traceId,
    hotelInfo,
    selectedRoomSummary,
    bookingDetails,
    allocateResponse,
    guestDetailsDraft,
  } = location.state || {};

  const [detailsLoading, setDetailsLoading] = useState(true);
  const [itineraryDetails, setItineraryDetails] = useState(null);
  const [detailsError, setDetailsError] = useState(null);

  const [orderFlowLoading, setOrderFlowLoading] = useState(false);
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [paymentModes, setPaymentModes] = useState([]);
  const [createOrderResult, setCreateOrderResult] = useState(null);
  const [paymentInitLoading, setPaymentInitLoading] = useState(false);

  useEffect(() => {
    const load = async () => {
      if (!itineraryCode || !traceId) {
        setItineraryDetails(MOCK_ITINERARY_DETAILS);
        setDetailsLoading(false);
        setDetailsError(null);
        return;
      }
      try {
        const response = await searchAPI.getHotelItineraryDetails({
          itineraryCode,
          traceId,
          includeGuestRules: true,
          includeSearchLog: true,
          includeStaticContent: true,
        });
        setItineraryDetails(response);
      } catch (e) {
        console.error(e);
        setItineraryDetails(MOCK_ITINERARY_DETAILS);
        setDetailsError(null);
        toast.error(
          e?.response?.data?.message ||
            "Unable to sync the latest details. Showing your saved itinerary."
        );
      } finally {
        setDetailsLoading(false);
      }
    };
    load();
  }, [itineraryCode, traceId]);

  const handleProceedToBooking = async () => {
    if (!itineraryCode || !traceId) {
      toast.error("Missing itinerary. Go back and complete the booking flow.");
      return;
    }
    if (!bookingDetails?.query) {
      toast.error("Search details are missing. Go back and try again.");
      return;
    }

    setOrderFlowLoading(true);
    try {
      const query = buildCreateOrderQuery(bookingDetails.query, traceId);
      const createRes = await searchAPI.createOrderFromItinerary({
        query,
        itineraryCode,
        traceId,
      });

      const createOk =
        createRes?.status === "success" || Number(createRes?.statusCode) === 200;
      if (!createOk) {
        toast.error(createRes?.message || "Could not create order.");
        return;
      }

      setCreateOrderResult(createRes?.data ?? null);

      const modesRes = await searchAPI.getPaymentModes();
      const modesOk =
        modesRes?.status === "success" || Number(modesRes?.statusCode) === 200;
      const rawModes = Array.isArray(modesRes?.data) ? modesRes.data : [];
      const modes = rawModes.filter((m) => m?.isActive !== false);

      if (!modesOk) {
        toast.warning(
          modesRes?.message || "Order created but payment options could not be loaded."
        );
      } else {
        // toast.success(
        //   createRes?.message || "Order created. Choose a payment method."
        // );
      }

      setPaymentModes(modes);
      setPaymentModalOpen(true);
    } catch (error) {
      console.error(error);
      toast.error(
        error?.response?.data?.message ||
          error?.message ||
          "Could not proceed. Try again."
      );
    } finally {
      setOrderFlowLoading(false);
    }
  };

  const handleClosePaymentModal = () => {
    if (paymentInitLoading) return;
    setPaymentModalOpen(false);
  };

  const handleSelectPaymentMode = async (mode) => {
    const useWallet = isWalletPaymentMode(mode);
    const useHdfc = isHdfcPaymentMode(mode);
    if (!useHdfc && !useWallet) {
      toast.info("This payment option will be connected soon.");
      return;
    }

    const orderNumber = createOrderResult?.order?.orderNumber;
    const amountRaw =
      createOrderResult?.payment?.amount ?? createOrderResult?.order?.totalAmount;
    if (!orderNumber || amountRaw == null) {
      toast.error("Order details are missing. Close and try proceeding again.");
      return;
    }

    const userIdStr = String(localStorage.getItem("userId") ?? "5");
    const loginId = localStorage.getItem("userName") || "";
    const customer_email = loginId.includes("@")
      ? loginId
      : "guest@example.com";
    const { first_name, last_name } = splitNameFromLoginEmail(loginId);

    const payload = {
      pgName: useHdfc ? "HDFC" : "wallet",
      order_id: orderNumber,
      amount: String(Number(amountRaw)),
      customer_id: userIdStr,
      customer_email,
      customer_phone: "9999999999",
      payment_page_client_id: "hdfcmaster",
      action: "paymentPage",
      // return_url: getPaymentReturnUrl(),
      return_url: getPaymentReturnUrl(),
      description: "Complete your payment",
      first_name,
      last_name,
    };

    try {
      sessionStorage.setItem(
        PENDING_BOOK_STORAGE_KEY,
        JSON.stringify({
          itineraryCode,
          traceId,
          query: buildCreateOrderQuery(bookingDetails.query, traceId),
          numericOrderId: createOrderResult?.order?.orderId ?? null,
          orderNumber,
        })
      );
    } catch (e) {
      console.warn("Could not persist book context", e);
    }

    setPaymentInitLoading(true);
    try {
      const res = await searchAPI.initiatePayment(payload);
      const ok =
        res?.status === "success" || Number(res?.statusCode) === 200;
      if (!ok) {
        toast.error(res?.message || "Payment could not be started.");
        return;
      }
      // toast.success(res?.message || "Payment initiated.");
      const data = res?.data ?? res;
      const redirect =
        data?.paymentUrl ||
        data?.paymentURL ||
        data?.redirectUrl ||
        data?.url;
      const hasHttpRedirect =
        typeof redirect === "string" &&
        (redirect.startsWith("http://") || redirect.startsWith("https://"));

      if (hasHttpRedirect) {
        window.location.href = redirect;
      } else if (useWallet) {
        const orderIdForStatus = data?.orderId || orderNumber;
        if (!orderIdForStatus) {
          toast.error("Order reference missing after wallet payment.");
          return;
        }
        setPaymentModalOpen(false);
        navigate(
          `/payment-status?order_id=${encodeURIComponent(String(orderIdForStatus))}&pgName=wallet`
        );
      } else {
        toast.error(
          res?.message ||
            "Payment could not be redirected. Try again or pick another method."
        );
      }
    } catch (error) {
      console.error(error);
      toast.error(
        error?.response?.data?.message ||
          error?.message ||
          "Could not start payment."
      );
    } finally {
      setPaymentInitLoading(false);
    }
  };

  const d = itineraryDetails?.data;

  const roomsForDisplay = useMemo(() => normalizeItineraryRooms(d), [d]);

  const rateForBreakdown = useMemo(() => {
    if (d?.rate && (d.rate.baseRate != null || d.rate.finalRate != null)) {
      return d.rate;
    }
    return aggregateRatesFromRooms(roomsForDisplay);
  }, [d, roomsForDisplay]);

  const cancellationRows = useMemo(
    () => flattenCancellationRows(roomsForDisplay),
    [roomsForDisplay]
  );

  const heroUrl = useMemo(
    () =>
      getHotelHeroImage(d?.hotel) ||
      hotelInfo?.heroImage ||
      hotelInfo?.images?.[0],
    [d?.hotel, hotelInfo]
  );

  const hotelName = d?.hotel?.name || hotelInfo?.name || "—";
  const roomName =
    roomsForDisplay.length > 1
      ? `${roomsForDisplay.length} rooms`
      : roomsForDisplay[0]?.name ||
        d?.room?.name ||
        selectedRoomSummary?.roomName ||
        "—";
  const checkIn =
    d?.searchDetails?.checkIn || bookingDetails?.query?.checkIn || "—";
  const checkOut =
    d?.searchDetails?.checkOut || bookingDetails?.query?.checkOut || "—";
  const totalFromApi = d?.priceInfo?.totalAmount;
  const currency =
    d?.priceInfo?.currency ||
    rateForBreakdown?.currency ||
    selectedRoomSummary?.currency ||
    "INR";

  const guestCountDisplay = useMemo(() => {
    const fromRooms = roomsForDisplay.reduce(
      (acc, r) => acc + (Array.isArray(r.guests) ? r.guests.length : 0),
      0
    );
    if (fromRooms > 0) return fromRooms;
    return (
      selectedRoomSummary?.guestCount ??
      bookingDetails?.guestCount ??
      (d?.searchDetails?.occupancies?.length
        ? d.searchDetails.occupancies.reduce((acc, o) => {
            const a = Number(o.adults ?? o.numOfAdults ?? 0) || 0;
            const c =
              (o.childAges || []).length ||
              Number(o.numOfChildren || 0) ||
              0;
            return acc + a + c;
          }, 0)
        : "—")
    );
  }, [
    roomsForDisplay,
    selectedRoomSummary?.guestCount,
    bookingDetails?.guestCount,
    d?.searchDetails?.occupancies,
  ]);

  const addressLine = d?.hotel?.address
    ? [
        d.hotel.address.line1,
        d.hotel.address.city,
        d.hotel.address.country,
        d.hotel.address.postalCode,
      ]
        .filter(Boolean)
        .join(", ")
    : hotelInfo?.address
    ? [
        hotelInfo.address.line1,
        hotelInfo.address.city,
        hotelInfo.address.country,
      ]
        .filter(Boolean)
        .join(", ")
    : "";

  const starValue = Math.min(5, Math.max(0, Number(d?.hotel?.starRating) || 0));

  const nights =
    checkIn && checkOut && checkIn !== "—" && checkOut !== "—"
      ? Math.max(
          1,
          Math.round(
            (new Date(checkOut) - new Date(checkIn)) / (1000 * 60 * 60 * 24)
          )
        )
      : null;

  return (
    <>
      <ToastContainer position="top-right" autoClose={3000} />
      <Box
        sx={{
          minHeight: "100vh",
          py: 4,
          mt: 2,
          bgcolor: "#f0f4f8",
          backgroundImage:
            "linear-gradient(180deg, #f0f4f8 0%, #ffffff 420px, #f5f7fa 100%)",
        }}
      >
        <Container
          maxWidth="xl"
          sx={{
            position: "relative",
            px: { xs: 2, sm: 3, md: 4 },
            py: 3,
            bgcolor: "white",
            borderRadius: 2,
            boxShadow: "0 0 24px rgba(0,0,0,0.06)",
            overflow: "visible",
          }}
        >
          {detailsLoading && (
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="center"
              spacing={2}
              sx={{ py: 10 }}
            >
              <CircularProgress size={36} sx={{ color: themePrimary }} />
              <Typography color="text.secondary" fontWeight={500}>
                Preparing your booking summary…
              </Typography>
            </Stack>
          )}

          {!detailsLoading && d && (
            <Box sx={{ position: "relative", width: "100%" }}>
              <GuestDecorLeftColumn />
              <GuestDecorRightColumn />

              <Box
                sx={{
                  position: "relative",
                  zIndex: 1,
                  maxWidth: { xs: "100%", sm: 640, md: 720, lg: 800 },
                  mx: "auto",
                  px: { xs: 0, sm: 1, md: 2 },
                  mt: { xs: 2.5, sm: 3, md: 3.5 },
                }}
              >
                <Stack spacing={0}>
                  <Typography
                    variant="overline"
                    sx={{
                      color: themeAccent,
                      fontWeight: 700,
                      letterSpacing: 1.2,
                      mb: 0.5,
                      textAlign: { xs: "left", sm: "center" },
                      display: "block",
                    }}
                  >
                    Almost there
                  </Typography>
                  <Typography
                    variant="h4"
                    sx={{
                      fontWeight: 700,
                      color: themePrimary,
                      fontSize: { xs: "1.5rem", sm: "1.75rem" },
                      mb: 0.5,
                      textAlign: { xs: "left", sm: "center" },
                    }}
                  >
                    Review your booking
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                      mb: 3,
                      textAlign: { xs: "left", sm: "center" },
                      maxWidth: 560,
                      mx: { sm: "auto" },
                    }}
                  >
                    Check the details below and continue when you&apos;re ready to
                    confirm.
                  </Typography>
                </Stack>

                <Card
                  elevation={0}
                  sx={{
                    borderRadius: 2,
                    overflow: "hidden",
                    mb: 3,
                    border: "1px solid",
                    borderColor: "divider",
                    boxShadow: "0 8px 32px rgba(27, 60, 115, 0.12)",
                  }}
                >
                  <Box sx={{ position: "relative" }}>
                    {heroUrl ? (
                      <CardMedia
                        component="img"
                        height="260"
                        image={heroUrl}
                        alt={hotelName}
                        sx={{ objectFit: "cover" }}
                      />
                    ) : (
                      <Box
                        sx={{
                          height: 200,
                          background: `linear-gradient(135deg, ${themePrimary} 0%, ${themeAccent} 100%)`,
                        }}
                      />
                    )}
                    <Box
                      sx={{
                        position: "absolute",
                        inset: 0,
                        background:
                          "linear-gradient(to top, rgba(0,0,0,0.75) 0%, transparent 55%)",
                      }}
                    />
                    <Box
                      sx={{
                        position: "absolute",
                        bottom: 0,
                        left: 0,
                        right: 0,
                        p: 2.5,
                      }}
                    >
                      <Typography
                        variant="h5"
                        sx={{
                          color: "#fff",
                          fontWeight: 700,
                          textShadow: "0 2px 8px rgba(0,0,0,0.4)",
                          mb: 0.5,
                        }}
                      >
                        {hotelName}
                      </Typography>
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <Rating
                          value={starValue}
                          readOnly
                          size="small"
                          sx={{ color: "#FFD700" }}
                        />
                        {nights != null && (
                          <Chip
                            size="small"
                            label={`${nights} night${nights > 1 ? "s" : ""}`}
                            sx={{
                              bgcolor: "rgba(255,255,255,0.2)",
                              color: "#fff",
                              fontWeight: 600,
                            }}
                          />
                        )}
                      </Stack>
                    </Box>
                  </Box>
                </Card>

                <Paper
                  elevation={0}
                  sx={{
                    p: { xs: 2, sm: 2.5 },
                    mb: 2,
                    borderRadius: 2,
                    border: "1px solid",
                    borderColor: "divider",
                    bgcolor: "#fafbfd",
                  }}
                >
                  <Stack
                    direction="row"
                    alignItems="flex-start"
                    spacing={1.5}
                    sx={{ mb: roomsForDisplay.length ? 2 : 0 }}
                  >
                    <Box
                      sx={{
                        p: 1,
                        borderRadius: 1.5,
                        bgcolor: "rgba(0, 113, 194, 0.08)",
                        color: themeAccent,
                      }}
                    >
                      <HotelIcon />
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Typography
                        variant="subtitle2"
                        color="text.secondary"
                        gutterBottom
                      >
                        {roomsForDisplay.length > 1 ? "Rooms" : "Room"}
                      </Typography>
                      {roomsForDisplay.length === 0 && (
                        <Typography variant="h6" fontWeight={600} color={themePrimary}>
                          {roomName}
                        </Typography>
                      )}
                    </Box>
                  </Stack>

                  {roomsForDisplay.map((roomEntry, idx) => (
                    <Box
                      key={`room-${roomEntry.index}-${roomEntry.name}`}
                      sx={{
                        mb: idx < roomsForDisplay.length - 1 ? 2.5 : 0,
                        pb: idx < roomsForDisplay.length - 1 ? 2.5 : 0,
                        borderBottom:
                          idx < roomsForDisplay.length - 1
                            ? "1px dashed"
                            : "none",
                        borderColor: "divider",
                      }}
                    >
                      <Stack
                        direction="row"
                        alignItems="center"
                        flexWrap="wrap"
                        gap={1}
                        sx={{ mb: 1 }}
                      >
                        <Chip
                          size="small"
                          label={`Room ${roomEntry.index}`}
                          sx={{
                            fontWeight: 700,
                            bgcolor: "rgba(0, 113, 194, 0.12)",
                            color: themePrimary,
                          }}
                        />
                        <Typography variant="h6" fontWeight={600} color={themePrimary}>
                          {roomEntry.name}
                        </Typography>
                      </Stack>
                      {roomEntry.guests?.length > 0 && (
                        <>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            fontWeight={700}
                            sx={{ display: "block", mb: 1 }}
                          >
                            Guests in this room
                          </Typography>
                          <TableContainer
                            component={Paper}
                            elevation={0}
                            sx={{
                              bgcolor: "#fff",
                              borderRadius: 1.5,
                              border: "1px solid",
                              borderColor: "divider",
                              boxShadow: "none",
                              overflow: "hidden",
                            }}
                          >
                            <Table size="small" sx={{ tableLayout: "fixed" }}>
                              <TableHead>
                                <TableRow>
                                  <TableCell
                                    sx={{
                                      fontWeight: 700,
                                      borderBottom: `1px solid ${tokens.border}`,
                                      py: 1,
                                      width: "32%",
                                    }}
                                  >
                                    Guest
                                  </TableCell>
                                  <TableCell
                                    sx={{
                                      fontWeight: 700,
                                      borderBottom: `1px solid ${tokens.border}`,
                                      py: 1,
                                      width: "14%",
                                    }}
                                  >
                                    Type
                                  </TableCell>
                                  <TableCell
                                    sx={{
                                      fontWeight: 700,
                                      borderBottom: `1px solid ${tokens.border}`,
                                      py: 1,
                                      width: "30%",
                                    }}
                                  >
                                    Email
                                  </TableCell>
                                  <TableCell
                                    sx={{
                                      fontWeight: 700,
                                      borderBottom: `1px solid ${tokens.border}`,
                                      py: 1,
                                      width: "24%",
                                    }}
                                  >
                                    Phone
                                  </TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {roomEntry.guests.map((g, gIdx, gArr) => {
                                  const rowBorder =
                                    gIdx < gArr.length - 1
                                      ? `1px solid ${tokens.border}`
                                      : "none";
                                  const phone =
                                    g?.isdCode && g?.contactNumber
                                      ? `+${String(g.isdCode).replace(/^\+/, "")} ${g.contactNumber}`
                                      : g?.contactNumber || "—";
                                  return (
                                    <TableRow key={g?.guestId ?? `${gIdx}-${g?.firstName}`}>
                                      <TableCell
                                        sx={{
                                          borderBottom: rowBorder,
                                          verticalAlign: "top",
                                          py: 1.15,
                                          fontSize: "0.875rem",
                                        }}
                                      >
                                        <Stack spacing={0.5}>
                                          <Typography variant="body2" fontWeight={600}>
                                            {guestDisplayName(g)}
                                          </Typography>
                                          {g?.isLeadGuest && (
                                            <Chip
                                              size="small"
                                              label="Lead guest"
                                              sx={{
                                                height: 22,
                                                fontSize: "0.7rem",
                                                fontWeight: 600,
                                                width: "fit-content",
                                              }}
                                            />
                                          )}
                                        </Stack>
                                      </TableCell>
                                      <TableCell
                                        sx={{
                                          borderBottom: rowBorder,
                                          verticalAlign: "top",
                                          py: 1.15,
                                          fontSize: "0.875rem",
                                          textTransform: "capitalize",
                                          color: "text.secondary",
                                        }}
                                      >
                                        {g?.guestType || "—"}
                                      </TableCell>
                                      <TableCell
                                        sx={{
                                          borderBottom: rowBorder,
                                          verticalAlign: "top",
                                          py: 1.15,
                                          fontSize: "0.8125rem",
                                          color: "text.secondary",
                                          wordBreak: "break-word",
                                        }}
                                      >
                                        {g?.email || "—"}
                                      </TableCell>
                                      <TableCell
                                        sx={{
                                          borderBottom: rowBorder,
                                          verticalAlign: "top",
                                          py: 1.15,
                                          fontSize: "0.8125rem",
                                          color: "text.secondary",
                                        }}
                                      >
                                        {phone}
                                      </TableCell>
                                    </TableRow>
                                  );
                                })}
                              </TableBody>
                            </Table>
                          </TableContainer>
                        </>
                      )}
                    </Box>
                  ))}

                  {roomsForDisplay.length === 0 && d.rate?.boardBasis?.description && (
                    <Chip
                      size="small"
                      label={d.rate.boardBasis.description}
                      sx={{ mt: 1, fontWeight: 500 }}
                      color="primary"
                      variant="outlined"
                    />
                  )}

                  {addressLine && (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ pl: 0.5, mt: roomsForDisplay.length ? 2 : 1 }}
                    >
                      {addressLine}
                    </Typography>
                  )}
                </Paper>

                <Paper
                  elevation={0}
                  sx={{
                    p: { xs: 2, sm: 2.5 },
                    mb: 2,
                    borderRadius: 2,
                    border: "1px solid",
                    borderColor: "divider",
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    fontWeight={700}
                    color={themePrimary}
                    sx={{ mb: 2 }}
                  >
                    Your stay
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <Paper
                        variant="outlined"
                        sx={{
                          p: 2,
                          borderRadius: 2,
                          borderColor: "rgba(0, 113, 194, 0.25)",
                          bgcolor: "rgba(0, 113, 194, 0.03)",
                        }}
                      >
                        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                          <CalendarMonthIcon sx={{ color: themeAccent, fontSize: 22 }} />
                          <Typography variant="caption" color="text.secondary" fontWeight={600}>
                            Check-in
                          </Typography>
                        </Stack>
                        <Typography variant="body1" fontWeight={600}>
                          {formatStayDate(checkIn)}
                        </Typography>
                      </Paper>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Paper
                        variant="outlined"
                        sx={{
                          p: 2,
                          borderRadius: 2,
                          borderColor: "rgba(0, 113, 194, 0.25)",
                          bgcolor: "rgba(0, 113, 194, 0.03)",
                        }}
                      >
                        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                          <CalendarMonthIcon sx={{ color: themeAccent, fontSize: 22 }} />
                          <Typography variant="caption" color="text.secondary" fontWeight={600}>
                            Check-out
                          </Typography>
                        </Stack>
                        <Typography variant="body1" fontWeight={600}>
                          {formatStayDate(checkOut)}
                        </Typography>
                      </Paper>
                    </Grid>
                  </Grid>
                  <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 2 }}>
                    <PeopleOutlineIcon sx={{ color: themeAccent }} />
                    <Typography variant="body2">
                      <strong>{guestCountDisplay}</strong> guest
                      {Number(guestCountDisplay) !== 1 ? "s" : ""}
                    </Typography>
                  </Stack>
                  {/* {d.traceDetail && (
                    <Stack
                      direction="row"
                      alignItems="center"
                      spacing={1}
                      sx={{ mt: 1.5, pt: 2, borderTop: "1px dashed", borderColor: "divider" }}
                    >
                      <TimerOutlinedIcon sx={{ fontSize: 20, color: "text.secondary" }} />
                      <Typography variant="caption" color="text.secondary">
                        Hold this price for{" "}
                        <strong>{d.traceDetail.remainingTime}s</strong>
                        {d.traceDetail.expiryTime && (
                          <> · Refreshes by {new Date(d.traceDetail.expiryTime).toLocaleString()}</>
                        )}
                      </Typography>
                    </Stack>
                  )} */}
                </Paper>

                {(rateForBreakdown?.baseRate != null ||
                  rateForBreakdown?.finalRate != null) && (
                  <Paper
                    elevation={0}
                    sx={{
                      p: { xs: 2, sm: 2.5 },
                      mb: 2,
                      borderRadius: 2,
                      border: "1px solid",
                      borderColor: "divider",
                    }}
                  >
                    <Typography
                      variant="subtitle2"
                      fontWeight={700}
                      color={themePrimary}
                      sx={{ mb: 2 }}
                    >
                      Price breakdown
                    </Typography>
                    <Stack spacing={1.25}>
                      <Stack direction="row" justifyContent="space-between">
                        <Typography variant="body2" color="text.secondary">
                          Room rate
                        </Typography>
                        <Typography variant="body2" fontWeight={500}>
                          {formatInr(rateForBreakdown?.baseRate)}
                        </Typography>
                      </Stack>
                      {rateForBreakdown?.taxAmount != null && (
                        <Stack direction="row" justifyContent="space-between">
                          <Typography variant="body2" color="text.secondary">
                            Taxes & fees
                          </Typography>
                          <Typography variant="body2" fontWeight={500}>
                            {formatInr(rateForBreakdown.taxAmount)}
                          </Typography>
                        </Stack>
                      )}
                      <Typography variant="caption" color="text.secondary" sx={{ display: "block", pt: 0.5 }}>
                        {rateForBreakdown?.refundable
                          ? "Refundable rate"
                          : "Non-refundable rate"}
                      </Typography>
                    </Stack>
                  </Paper>
                )}

                {cancellationRows.length > 0 && (
                  <Accordion
                    disableGutters
                    elevation={0}
                    sx={{
                      mb: 2,
                      border: "1px solid",
                      borderColor: "divider",
                      borderRadius: "12px !important",
                      "&:before": { display: "none" },
                      overflow: "hidden",
                    }}
                  >
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <ShieldOutlinedIcon color="primary" />
                        <Typography fontWeight={600} color={themePrimary}>
                          Cancellation policy
                        </Typography>
                      </Stack>
                    </AccordionSummary>
                    <AccordionDetails
                      sx={{
                        pt: 0,
                        px: { xs: 1.5, sm: 2 },
                        pb: 2,
                        bgcolor: "#f5f7fb",
                      }}
                    >
                      <TableContainer
                        component={Paper}
                        elevation={0}
                        sx={{
                          bgcolor: "#fff",
                          borderRadius: 1.5,
                          border: "1px solid",
                          borderColor: "divider",
                          boxShadow: "none",
                          overflow: "hidden",
                        }}
                      >
                        <Table size="small" sx={{ tableLayout: "fixed" }}>
                          <TableHead>
                            <TableRow>
                              <TableCell
                                sx={{
                                  fontWeight: 700,
                                  color: "text.primary",
                                  borderBottom: `1px solid ${tokens.border}`,
                                  borderRight: "none",
                                  py: 1.25,
                                  width: "18%",
                                }}
                              >
                                Room
                              </TableCell>
                              <TableCell
                                sx={{
                                  fontWeight: 700,
                                  color: "text.primary",
                                  borderBottom: `1px solid ${tokens.border}`,
                                  borderRight: "none",
                                  py: 1.25,
                                  width: "28%",
                                }}
                              >
                                Start Date
                              </TableCell>
                              <TableCell
                                sx={{
                                  fontWeight: 700,
                                  color: "text.primary",
                                  borderBottom: `1px solid ${tokens.border}`,
                                  borderRight: "none",
                                  py: 1.25,
                                  width: "28%",
                                }}
                              >
                                End Date
                              </TableCell>
                              <TableCell
                                sx={{
                                  fontWeight: 700,
                                  color: "text.primary",
                                  borderBottom: `1px solid ${tokens.border}`,
                                  borderRight: "none",
                                  py: 1.25,
                                  width: "26%",
                                }}
                              >
                                Charges
                              </TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {cancellationRows.map(({ roomLabel, rule }, idx) => {
                              const rowBorder =
                                idx < cancellationRows.length - 1
                                  ? `1px solid ${tokens.border}`
                                  : "none";
                              return (
                                <TableRow key={`${roomLabel}-${idx}-${rule?.start}`}>
                                  <TableCell
                                    sx={{
                                      borderBottom: rowBorder,
                                      borderRight: "none",
                                      verticalAlign: "top",
                                      py: 1.25,
                                      color: themePrimary,
                                      fontSize: "0.875rem",
                                      fontWeight: 700,
                                    }}
                                  >
                                    {roomLabel}
                                  </TableCell>
                                  <TableCell
                                    sx={{
                                      borderBottom: rowBorder,
                                      borderRight: "none",
                                      verticalAlign: "top",
                                      py: 1.25,
                                      color: "text.secondary",
                                      fontSize: "0.875rem",
                                    }}
                                  >
                                    {formatCancellationDateTime(rule.start)}
                                  </TableCell>
                                  <TableCell
                                    sx={{
                                      borderBottom: rowBorder,
                                      borderRight: "none",
                                      verticalAlign: "top",
                                      py: 1.25,
                                      color: "text.secondary",
                                      fontSize: "0.875rem",
                                    }}
                                  >
                                    {formatCancellationDateTime(rule.end)}
                                  </TableCell>
                                  <TableCell
                                    sx={{
                                      borderBottom: rowBorder,
                                      borderRight: "none",
                                      verticalAlign: "top",
                                      py: 1.25,
                                      color: "text.secondary",
                                      fontSize: "0.875rem",
                                      fontWeight: 500,
                                    }}
                                  >
                                    {formatCancellationCharge(rule)}
                                  </TableCell>
                                </TableRow>
                              );
                            })}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </AccordionDetails>
                  </Accordion>
                )}

                {d.descriptions?.[0]?.text && (
                  <Paper
                    elevation={0}
                    sx={{
                      p: { xs: 2, sm: 2.5 },
                      mb: 2,
                      borderRadius: 2,
                      border: "1px solid",
                      borderColor: "divider",
                      bgcolor: "#fcfcfd",
                    }}
                  >
                    <Typography
                      variant="subtitle2"
                      fontWeight={700}
                      color={themePrimary}
                      sx={{ mb: 1.5 }}
                    >
                      About this property
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ lineHeight: 1.8 }}
                    >
                      {d.descriptions[0].text.length > 520
                        ? `${d.descriptions[0].text.slice(0, 520)}…`
                        : d.descriptions[0].text}
                    </Typography>
                  </Paper>
                )}

                {/* {(d.guestRules || d.bookingRequirements) && (
                  <Paper
                    elevation={0}
                    sx={{
                      p: { xs: 2, sm: 2.5 },
                      mb: 2,
                      borderRadius: 2,
                      border: "1px solid",
                      borderColor: "rgba(27, 60, 115, 0.12)",
                      background:
                        "linear-gradient(135deg, rgba(27,60,115,0.04) 0%, rgba(0,113,194,0.04) 100%)",
                    }}
                  >
                    <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
                      <InfoOutlinedIcon sx={{ color: themePrimary }} />
                      <Typography fontWeight={700} color={themePrimary}>
                        Important information
                      </Typography>
                    </Stack>
                    {d.guestRules?.leadGuest &&
                      (d.guestRules.leadGuest.emailRequired ||
                        d.guestRules.leadGuest.phoneRequired) && (
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        Lead guest must provide{" "}
                        {[
                          d.guestRules.leadGuest.emailRequired && "email",
                          d.guestRules.leadGuest.phoneRequired && "phone number",
                        ]
                          .filter(Boolean)
                          .join(" and ")}
                        .
                      </Typography>
                    )}
                    {d.bookingRequirements && (
                      <Typography variant="body2" color="text.secondary">
                        PAN: {d.bookingRequirements.panMandatory ? "Required" : "Not required"} ·
                        Passport:{" "}
                        {d.bookingRequirements.passportMandatory ? "Required" : "Not required"}
                      </Typography>
                    )}
                  </Paper>
                )} */}

                {d.facilities?.length > 0 && (
                  <Paper
                    elevation={0}
                    sx={{
                      p: { xs: 2, sm: 2.5 },
                      mb: 2,
                      borderRadius: 2,
                      border: "1px solid",
                      borderColor: "divider",
                    }}
                  >
                    <Typography
                      variant="subtitle2"
                      fontWeight={700}
                      color={themePrimary}
                      sx={{ mb: 1.5 }}
                    >
                      Amenities & facilities
                    </Typography>
                    <Stack direction="row" flexWrap="wrap" gap={0.75}>
                      {d.facilities.slice(0, 20).map((f) => (
                        <Chip
                          key={`${f.id}-${f.name}-${f.groupId}`}
                          size="small"
                          label={f.name}
                          sx={{
                            borderRadius: "8px",
                            bgcolor: "grey.100",
                            fontWeight: 500,
                          }}
                        />
                      ))}
                      {d.facilities.length > 20 && (
                        <Chip
                          size="small"
                          label={`+${d.facilities.length - 20} more`}
                          variant="outlined"
                        />
                      )}
                    </Stack>
                  </Paper>
                )}
              </Box>

              <Box
                sx={{
                  position: "relative",
                  zIndex: 1,
                  maxWidth: { xs: "100%", sm: 640, md: 720, lg: 800 },
                  mx: "auto",
                  mt: { xs: 3, md: 4 },
                  px: { xs: 0, sm: 1, md: 2 },
                }}
              >
                <Paper
                  elevation={0}
                  sx={{
                    p: { xs: 2, sm: 2.5, md: 3 },
                    borderRadius: 2,
                    border: "2px solid",
                    borderColor: "rgba(0, 113, 194, 0.2)",
                    boxShadow: "0 12px 40px rgba(27, 60, 115, 0.08)",
                    background: "linear-gradient(180deg, #ffffff 0%, #f8fbff 100%)",
                  }}
                >
                  <Stack
                    direction={{ xs: "column", sm: "row" }}
                    spacing={2.5}
                    alignItems={{ xs: "stretch", sm: "center" }}
                    justifyContent="space-between"
                  >
                    <Box sx={{ flex: { sm: "1 1 auto" }, minWidth: 0 }}>
                      <Typography
                        variant="overline"
                        sx={{ color: themeAccent, fontWeight: 700, letterSpacing: 1, display: "block" }}
                      >
                        Total for your stay
                      </Typography>
                      <Typography
                        variant="h4"
                        sx={{
                          fontWeight: 800,
                          color: "#0d7a3e",
                          my: 0.5,
                          fontSize: { xs: "1.65rem", sm: "1.85rem" },
                          lineHeight: 1.2,
                        }}
                      >
                   {
  itineraryDetails?.data?.rooms?.[0]?.rate?.finalRate != null
    ? `${currency === "INR" ? "₹" : `${currency} `}${Number(
        itineraryDetails.data.rooms[0].rate.finalRate
      ).toLocaleString("en-IN")}`
    : totalFromApi != null
    ? `${currency === "INR" ? "₹" : `${currency} `}${Number(
        totalFromApi
      ).toLocaleString("en-IN")}`
    : selectedRoomSummary?.finalRate != null
    ? formatInr(selectedRoomSummary.finalRate)
    : formatInr(rateForBreakdown?.finalRate)
}
                      </Typography>
                      <Stack spacing={0.75} sx={{ mt: 1 }}>
                        <Stack direction="row" justifyContent="space-between" gap={1}>
                          <Typography variant="body2" color="text.secondary">
                            Hotel
                          </Typography>
                          <Typography
                            variant="body2"
                            fontWeight={600}
                            align="right"
                            sx={{ maxWidth: "58%" }}
                            noWrap
                            title={hotelName}
                          >
                            {hotelName}
                          </Typography>
                        </Stack>
                        <Stack direction="row" justifyContent="space-between" gap={1}>
                          <Typography variant="body2" color="text.secondary">
                            {roomsForDisplay.length > 1 ? "Rooms" : "Room"}
                          </Typography>
                          <Typography
                            variant="body2"
                            fontWeight={500}
                            align="right"
                            sx={{ maxWidth: "58%" }}
                            noWrap
                            title={
                              roomsForDisplay.length > 1
                                ? roomsForDisplay
                                    .map((r) => `Room ${r.index}: ${r.name}`)
                                    .join("\n")
                                : roomName
                            }
                          >
                            {roomName}
                          </Typography>
                        </Stack>
                      </Stack>
                    </Box>
                    <Divider
                      sx={{ display: { xs: "block", sm: "none" }, width: "100%" }}
                    />
                    <Divider
                      sx={{ display: { xs: "none", sm: "block" }, alignSelf: "stretch" }}
                      orientation="vertical"
                      flexItem
                    />
                    <Stack
                      spacing={1.5}
                      sx={{
                        width: { xs: "100%", sm: "auto" },
                        minWidth: { sm: 220, md: 260 },
                        flexShrink: 0,
                      }}
                    >
                      <Button
                        fullWidth
                        variant="contained"
                        size="large"
                        onClick={handleProceedToBooking}
                        disabled={orderFlowLoading}
                        sx={{
                          textTransform: "none",
                          fontWeight: 700,
                          py: 1.35,
                          fontSize: "1rem",
                          bgcolor: themePrimary,
                          boxShadow: "0 4px 14px rgba(27, 60, 115, 0.35)",
                          "&:hover": {
                            bgcolor: "#142d56",
                            boxShadow: "0 6px 20px rgba(27, 60, 115, 0.4)",
                          },
                        }}
                      >
                        {orderFlowLoading ? (
                          <Stack direction="row" alignItems="center" justifyContent="center" spacing={1}>
                            <CircularProgress size={22} sx={{ color: "common.white" }} />
                            <span>Creating order…</span>
                          </Stack>
                        ) : (
                          "Proceed to booking"
                        )}
                      </Button>
                      <Button
                        fullWidth
                        variant="outlined"
                        sx={{ textTransform: "none", fontWeight: 600, borderColor: "divider" }}
                        onClick={() =>
                          navigate("/guest-detail", {
                            state: {
                              bookingDetails,
                              hotelInfo,
                              selectedRoomSummary,
                              guestDetailsDraft,
                            },
                          })
                        }
                      >
                        Back to edit details
                      </Button>
                    </Stack>
                  </Stack>
                </Paper>
              </Box>
            </Box>
          )}

          {!detailsLoading && detailsError && !d && (
            <Typography color="error" sx={{ py: 4, textAlign: "center" }}>
              {detailsError}
            </Typography>
          )}

        </Container>
      </Box>

      <Dialog
        open={paymentModalOpen}
        onClose={handleClosePaymentModal}
        fullWidth
        maxWidth="sm"
        disableEscapeKeyDown={paymentInitLoading}
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1,
            pr: 1,
            fontWeight: 700,
            color: themePrimary,
          }}
        >
          <Stack direction="row" alignItems="center" spacing={1}>
            <PaymentOutlinedIcon sx={{ color: themeAccent }} />
            <span>Choose payment method</span>
          </Stack>
          <IconButton
            onClick={handleClosePaymentModal}
            aria-label="Close"
            size="small"
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {createOrderResult?.order?.orderNumber && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Order reference:{" "}
              <strong>{createOrderResult.order.orderNumber}</strong>
            </Typography>
          )}
          {paymentModes.length === 0 ? (
            <Typography color="text.secondary">
              No active payment options are available. You can close this dialog
              and try again later.
            </Typography>
          ) : (
            <Stack spacing={1.5}>
              {paymentInitLoading && (
                <Stack direction="row" alignItems="center" spacing={1.5} sx={{ py: 1 }}>
                  <CircularProgress size={22} sx={{ color: themePrimary }} />
                  <Typography variant="body2" color="text.secondary" fontWeight={600}>
                    Starting payment…
                  </Typography>
                </Stack>
              )}
              {paymentModes.map((mode) => {
                const hdfc = isHdfcPaymentMode(mode);
                const phonePe = !hdfc && isPhonePePaymentMode(mode);
                const wallet = !hdfc && !phonePe && isWalletPaymentMode(mode);
                const canPayNow = hdfc || wallet;

                const methodIconBox = (
                  <Box
                    sx={{
                      width: 52,
                      height: 52,
                      flexShrink: 0,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      borderRadius: 1.5,
                      bgcolor: "#fff",
                      border: "1px solid",
                      borderColor: "divider",
                      overflow: "hidden",
                    }}
                  >
                    {hdfc ? (
                      <Box
                        component="img"
                        src={PAYMENT_BRAND_HDFC_IMG}
                        alt="HDFC Bank"
                        sx={{ maxWidth: "88%", maxHeight: 36, objectFit: "contain" }}
                      />
                    ) : phonePe ? (
                      <Box
                        component="img"
                        src={PAYMENT_BRAND_PHONEPE_IMG}
                        alt="PhonePe"
                        sx={{ maxWidth: "92%", maxHeight: 28, objectFit: "contain" }}
                      />
                    ) : wallet ? (
                      <AccountBalanceWalletIcon
                        sx={{ fontSize: 30, color: themeAccent }}
                        aria-hidden
                      />
                    ) : (
                      <PaymentOutlinedIcon
                        sx={{ fontSize: 26, color: themeAccent, opacity: 0.85 }}
                        aria-hidden
                      />
                    )}
                  </Box>
                );

                return (
                  <Button
                    key={mode.id}
                    fullWidth
                    type="button"
                    variant="outlined"
                    onClick={() => handleSelectPaymentMode(mode)}
                    disabled={paymentInitLoading}
                    sx={{
                      p: 2,
                      borderRadius: 2,
                      borderColor: "divider",
                      bgcolor: "#fafbfd",
                      justifyContent: "flex-start",
                      textTransform: "none",
                      "&:hover": { bgcolor: "#f0f4f8", borderColor: themeAccent },
                    }}
                  >
                    <Stack
                      direction="row"
                      alignItems="center"
                      spacing={1.5}
                      sx={{ width: "100%" }}
                    >
                      {methodIconBox}
                      <Box sx={{ flex: 1, textAlign: "left" }}>
                        <Typography fontWeight={700} color={themePrimary}>
                          {mode.paymentModeName || mode.code || "Payment"}
                        </Typography>
                        {mode.code &&
                          mode.paymentModeName &&
                          mode.paymentModeName !== mode.code && (
                            <Typography variant="caption" color="text.secondary" display="block">
                              {mode.code}
                            </Typography>
                          )}
                        {!canPayNow && (
                          <Typography variant="caption" color="text.secondary">
                            Coming soon
                          </Typography>
                        )}
                      </Box>
                      {canPayNow && (
                        <Typography variant="caption" fontWeight={700} color={themeAccent}>
                          Pay
                        </Typography>
                      )}
                    </Stack>
                  </Button>
                );
              })}
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            onClick={handleClosePaymentModal}
            variant="outlined"
            sx={{ textTransform: "none", fontWeight: 600 }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ReviewBooking;
